import React, { useState, useEffect } from 'react';
import crm from '../crm/api';
import { Users, Search, Plus, Edit, Trash2, Eye, ExternalLink } from 'lucide-react';
import AddEmployeeModal from '../crm/AddEmployeeModal';

export default function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);   // the employee being edited

  useEffect(() => {
    fetchEmployees();
  }, [searchTerm, statusFilter, pagination.page]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: statusFilter,
        search: searchTerm || undefined
      };

      const response = await crm.get('/employees', { params });
      
      if (response.data.success) {
        const list = response.data.employees || [];
        setEmployees(list);
        // Keep the previous shape if the server omits pagination — reading
        // .page off undefined blanks the whole screen on the next render.
        setPagination((prev) => response.data.pagination || { ...prev, total: list.length, pages: 1 });
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination({ ...pagination, page: 1 }); // Reset to page 1 on search
  };
  
const handleViewProfile = (slug) => {
  window.open(`/profile/${slug}`, '_blank');
};

  const handleDelete = async (id, name) => {
    // Deactivation, not deletion — their leads and history stay intact.
    if (window.confirm(`Deactivate ${name}? Their login stops working and their card goes offline, but their leads are kept.`)) {
      try {
        await crm.delete(`/employees/${id}`);
        fetchEmployees();
        alert('Employee deactivated');
      } catch (error) {
        alert('Failed to deactivate employee: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text)]">Employees</h1>
          <p className="text-[var(--muted)] mt-1">Manage employee profiles and microsites</p>
        </div>
        <button onClick={() => setAdding(true)} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-2">
          <Plus size={20} />
          <span>Add Employee</span>
        </button>
      </div>

      {/* Filters & Search */}
      <div className="bg-[var(--card)] rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, email, or slug..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          >
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
            <option value="all">All Employees</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[var(--card)] rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--muted)]">Total Employees</p>
              <p className="text-2xl font-bold text-[var(--text)]">{pagination.total}</p>
            </div>
            <Users className="text-teal-500" size={32} />
          </div>
        </div>
        <div className="bg-[var(--card)] rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--muted)]">Active</p>
              <p className="text-2xl font-bold text-green-600">
                {employees.filter(e => e.isActive).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-xl">✓</span>
            </div>
          </div>
        </div>
        <div className="bg-[var(--card)] rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--muted)]">Page {pagination.page} of {pagination.pages}</p>
              <p className="text-2xl font-bold text-[var(--text)]">{employees.length}</p>
            </div>
            <div className="text-gray-400">
              <span className="text-sm">Showing</span>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Table */}
      <div className="bg-[var(--card)] rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-[var(--muted)]">Loading employees...</div>
          </div>
        ) : employees.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-[var(--muted)]">No employees found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[var(--soft)] border-b border-[var(--border)]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--muted)] uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--muted)] uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--muted)] uppercase tracking-wider">
                      Profile Slug
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--muted)] uppercase tracking-wider">
                      Content
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--muted)] uppercase tracking-wider">
                      Leads
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--muted)] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-[var(--muted)] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-[var(--card)] divide-y divide-[var(--border)]">
                  {employees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-[var(--soft)]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold">
                            {employee.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-[var(--text)]">{employee.name}</div>
                            <div className="text-sm text-[var(--muted)]">{employee.company || 'No company'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[var(--text)]">{employee.email}</div>
                        <div className="text-sm text-[var(--muted)]">{employee.whatsapp || 'No mobile'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm bg-[var(--soft)] px-2 py-1 rounded">
                          {employee.profileSlug}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-sm font-medium text-[var(--text)]">
                          {employee.content_count || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="text-sm font-medium text-teal-600">
                          {employee.lead_count || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          employee.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {employee.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewProfile(employee.profileSlug)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Profile"
                          >
                            <ExternalLink size={18} />
                          </button>
                          <button
                            onClick={() => setEditing(employee)}
                            className="text-teal-600 hover:text-teal-900"
                            title="Edit Employee"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(employee.id, employee.name)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Employee"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="bg-[var(--soft)] px-6 py-4 flex items-center justify-between border-t border-[var(--border)]">
                <div className="text-sm text-[var(--text)]">
                  Showing page {pagination.page} of {pagination.pages} 
                  <span className="font-medium"> ({pagination.total} total)</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 border border-[var(--border)] rounded-lg text-sm font-medium text-[var(--text)] bg-[var(--card)] hover:bg-[var(--soft)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page === pagination.pages}
                    className="px-4 py-2 border border-[var(--border)] rounded-lg text-sm font-medium text-[var(--text)] bg-[var(--card)] hover:bg-[var(--soft)] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    {adding && (
        <AddEmployeeModal
          onClose={() => setAdding(false)}
          onCreated={() => { setAdding(false); fetchEmployees(); }}
        />
      )}
    {editing && (
        <AddEmployeeModal
          employee={editing}
          onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); fetchEmployees(); }}
        />
      )}
    </div>
  );
}